import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import { getTokenRequests, toggleTokenStatus } from '../api/tokens';
import { Card } from '../components/ui-kit';
import TokenFormDialog from '../components/TokenFormDialog';
import TokenViewDialog from '../components/TokenViewDialog';
import { usePermissions } from '../context/PermissionsContext';
import { useAuth } from '../context/AuthContext';

function TokenRequests() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();

  const hasViewValueAll = hasPermission('auth-service', 'api-token', 'view-value');
  const hasViewValueOwn = hasPermission('auth-service', 'api-token', 'view-value-own');
  const hasUpdateAll = hasPermission('auth-service', 'api-token', 'update');
  const hasUpdateOwn = hasPermission('auth-service', 'api-token', 'update-own');

  const isMyToken = (token) =>
    token.ownerUserId?._id === user?.id || token.ownerUserId?.id === user?.id;

  const canViewValue = (token) => hasViewValueAll || (hasViewValueOwn && isMyToken(token));
  const canUpdate = (token) => hasUpdateAll || (hasUpdateOwn && isMyToken(token));

  const [editToken, setEditToken] = useState(null);
  const [viewToken, setViewToken] = useState(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['token-requests'],
    queryFn: () => getTokenRequests(),
  });

  const toggleStatusMutation = useMutation({
    mutationFn: toggleTokenStatus,
    onSuccess: (response) => {
      queryClient.invalidateQueries(['token-requests']);
      queryClient.invalidateQueries(['token-requests-count']);
      const message = response.data?.isActive ? 'Token activated' : 'Token deactivated';
      toast.success(message);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to toggle token status');
    },
  });

  const tokens = data?.data || [];

  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const isExpired = (expiresAt) => expiresAt && new Date(expiresAt) < new Date();

  if (isError) {
    return (
      <Typography color="error" sx={{ p: 2 }}>
        Error: {error?.response?.data?.message || error.message}
      </Typography>
    );
  }

  return (
    <>
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Token Name</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Prefix</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Expires</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center"><CircularProgress /></TableCell>
                </TableRow>
              ) : tokens.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Box sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No pending token requests. All tokens have permissions configured.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                tokens.map((token) => (
                  <TableRow key={token.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">{token.tokenName}</Typography>
                      {token.description && (
                        <Typography variant="caption" color="text.secondary">{token.description}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {token.ownerUserId?.fullName ||
                          (token.ownerUserId?.firstName || token.ownerUserId?.lastName
                            ? `${token.ownerUserId.firstName || ''} ${token.ownerUserId.lastName || ''}`.trim()
                            : token.ownerUserId?.email || '—')}
                      </Typography>
                      {token.ownerUserId?.email && (
                        <Typography variant="caption" color="text.secondary">
                          {token.ownerUserId.email}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">{token.tokenPrefix}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(token.createdAt)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color={isExpired(token.expiresAt) ? 'error' : 'text.primary'}>
                        {formatDate(token.expiresAt)}
                        {isExpired(token.expiresAt) && (
                          <Typography component="span" variant="caption" color="error" sx={{ ml: 0.5 }}>(expired)</Typography>
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        icon={token.isActive ? <CheckCircleIcon /> : <BlockIcon />}
                        label={token.isActive ? 'Active' : 'Revoked'}
                        color={token.isActive ? 'success' : 'error'}
                        size="small"
                        onClick={canUpdate(token) ? () => toggleStatusMutation.mutate(token.id) : undefined}
                        sx={{ cursor: canUpdate(token) ? 'pointer' : 'default' }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <Tooltip title="View details">
                          <IconButton size="small" color="primary" onClick={() => setViewToken(token)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Configure permissions">
                          <IconButton size="small" onClick={() => setEditToken(token)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {viewToken && (
        <TokenViewDialog
          open={!!viewToken}
          onClose={() => setViewToken(null)}
          token={viewToken}
          canViewValue={canViewValue(viewToken)}
        />
      )}

      {editToken && (
        <TokenFormDialog
          open={!!editToken}
          onClose={() => setEditToken(null)}
          token={editToken}
          onSuccess={() => {
            setEditToken(null);
            queryClient.invalidateQueries(['token-requests']);
            queryClient.invalidateQueries(['token-requests-count']);
            queryClient.invalidateQueries(['tokens']);
          }}
        />
      )}
    </>
  );
}

export default TokenRequests;
